import { ref, computed, Ref } from 'vue';
import { $http } from './stores/http';

let syncIntervalTimer = setInterval(forceSync, 1000 * 60 * 5); // Sync every 5 seconds
let initialSetTimeout;
let nextUpdateInterval;

const lastSync = ref();
const lastSyncDiff = ref();

document.addEventListener('visibilitychange', handleVisibilityChange);

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') forceSync();
}

type ResponseTimestampInNano = number;
type halfRttInNano = number;
type RequestTimeStampInMs = number;
type Accuracy = number;

type ServerTimeResponse = {
  localTime: RequestTimeStampInMs;
  serverTime: ResponseTimestampInNano;
  halfRtt: halfRttInNano;
};

type ServerTimeReponseWithAccuracy = {
  localTime: RequestTimeStampInMs;
  serverTime: ResponseTimestampInNano;
  halfRttMs: halfRttInNano;
  accuracy: Accuracy;
};
const localTimeUTC = ref(new Date());
const serverTimeUTC = ref(new Date());

const tzNameIANA = ref();

const displayTime = computed(() => {
  if (!tzNameIANA.value) return '...';
  const timeZone = tzNameIANA.value || 'UTC';

  return serverTimeUTC.value.toLocaleTimeString('en-US', {
    timeZone,
  });
});

const difference = computed(() =>
  calcuateDiff(serverTimeUTC.value, localTimeUTC.value)
);

async function getIana(): Promise<string> {
  try {
    const ianaResponse: string = await $http('GET', '/iana_timezone');

    // const data = await ianaResponse.json();
    return ianaResponse.replace(/^['"](.*)['"]$/, '$1'); // Remove quotes
  } catch (error) {
    console.error('Error fetching IANA timezone:', error);
  }

  return 'UTC';
}

async function getTime(): Promise<ServerTimeResponse> {
  const start = performance.now();
  const timestampResponse = await $http('GET', '/get_time');
  const end = performance.now();
  const timestampInNano = await timestampResponse.json();
  return {
    localTime: performance.timeOrigin + start,
    serverTime: timestampInNano,
    halfRtt: Math.ceil((end - start) / 2), // Approximate half RTT
  };
}

async function getMostAccurateResponse(): Promise<ServerTimeReponseWithAccuracy> {
  const promises = Array.from({ length: 10 }, getTime); // Collect multiple samples
  const results = await Promise.allSettled(promises);

  // Filter out invalid responses based on RTT deviation and calculate offsets directly
  const validResults = results
    .filter((result) => result.status === 'fulfilled')
    .map((x) => x.value); // Only keep successful responses

  if (validResults.length === 0) {
    // Return a fallback if no valid responses
    return {
      localTime: Date.now(),
      serverTime: Date.now() * 1_000_000, // Fallback to current time in nanoseconds
      halfRttMs: 0,
      accuracy: 0,
    };
  }

  // Calculate mean RTT and standard deviation
  const meanRtt =
    validResults.reduce((sum, result) => sum + result.halfRtt, 0) /
    validResults.length;
  const rttDeviation = Math.sqrt(
    validResults.reduce(
      (sum, result) => sum + Math.pow(result.halfRtt - meanRtt, 2),
      0
    ) / validResults.length
  );

  // Filter out responses with RTT too far from the mean (2 standard deviations)
  const filteredResults = validResults.filter(
    (result) => Math.abs(result.halfRtt - meanRtt) <= 2 * rttDeviation
  );

  if (filteredResults.length === 0) {
    // Fallback if no valid filtered responses
    const smallestRttResult = validResults.reduce((prev, curr) =>
      prev.halfRtt < curr.halfRtt ? prev : curr
    );
    return {
      localTime: smallestRttResult.localTime,
      serverTime: smallestRttResult.serverTime,
      halfRttMs: smallestRttResult.halfRtt,
      accuracy: 0,
    };
  }

  // Calculate mean offset from filtered responses
  const meanOffset =
    filteredResults.reduce((sum, result) => sum + result.halfRtt, 0) /
    filteredResults.length;

  // Calculate the accuracy (standard deviation of the filtered offsets)
  const accuracy = Math.sqrt(
    filteredResults.reduce(
      (sum, result) => sum + Math.pow(result.halfRtt - meanOffset, 2),
      0
    ) / filteredResults.length
  );

  console.log('Mean Offset:', meanOffset, 'ms');
  console.log('Accuracy (Standard Deviation):', accuracy, 'ms');

  // Return the most accurate response (based on filtered valid responses)
  const bestResult = filteredResults.reduce((prev, curr) =>
    prev.halfRtt < curr.halfRtt ? prev : curr
  );

  return {
    localTime: bestResult.localTime,
    serverTime: bestResult.serverTime,
    halfRttMs: bestResult.halfRtt,
    accuracy,
  };
}

async function getOffset(lastSyncRef?: Ref<Date>): Promise<number> {
  // Note localTime is in ms, serverTime is in ns
  // and halfRttMs is in ms
  const { localTime, serverTime, halfRttMs } = await getMostAccurateResponse();
  if (lastSyncRef) lastSyncRef.value = new Date(localTime); // Update the last sync time
  return serverTime - (localTime * 1_000_000 + halfRttMs); // Update the offset
}

function getAdjustedTime(offsetInNs: number) {
  const localTime = Date.now(); // Get the local time in milliseconds
  const adjustedTime = localTime + offsetInNs / 1_000_000; // Convert the offset from ns to ms and adjust
  return new Date(adjustedTime);
}

function update(amountInMs = 0) {
  serverTimeUTC.value.setMilliseconds(
    serverTimeUTC.value.getMilliseconds() + amountInMs
  );
  serverTimeUTC.value = new Date(serverTimeUTC.value);
  localTimeUTC.value = new Date();
}

async function forceSync() {
  console.debug('Force sync called');
  try {
    tzNameIANA.value = await getIana();
    const offsetResponse = await getOffset(lastSync);

    serverTimeUTC.value = getAdjustedTime(offsetResponse);
    localTimeUTC.value = new Date();

    lastSyncDiff.value =
      serverTimeUTC.value.getTime() - localTimeUTC.value.getTime();

    const delay =
      Math.abs(serverTimeUTC.value.getTime() - localTimeUTC.value.getTime()) /
      1000;

    if (initialSetTimeout) clearTimeout(initialSetTimeout);
    if (nextUpdateInterval) clearInterval(nextUpdateInterval);

    const frequencyMs = 100; // Update every second

    initialSetTimeout = setTimeout(() => {
      nextUpdateInterval = setInterval(() => update(frequencyMs), frequencyMs);
    }, delay);
  } catch (error) {
    console.error('Error fetching server time:', error);
    return;
  }
}

function calcuateDiff(serverTime: Date, localTime: Date) {
  return serverTime.getTime() - localTime.getTime();
}

const title = computed(() => {
  return `Server vs Local Difference: ${difference.value} ms\nServer Location: ${tzNameIANA.value}\n
   `;
});

export default {
  displayTime,
  difference,
  serverTimeUTC,
  localTimeUTC,
  tzNameIANA,
  forceSync,
  title,
};
